import {
  Alert,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { roomConfigProblem } from '@chipless/shared';
import { useState } from 'react';
import { createRoom } from './api.js';
import { AmountInput, parseAmount, type AmountValue } from './components/AmountInput.js';
import { AppTitle } from './components/AppTitle.js';
import classes from './LandingScreen.module.css';
import { QrScanner } from './QrScanner.js';
import { displayUrl, joinUrl, navigate } from './routing.js';

/**
 * QR を読み取れたら確認を挟まず参加へ進む。コードの形式は `roomCodeFromScannedText` が
 * 検証しており、関係のない QR はここへ来ない。
 *
 * コンポーネントの外に置いて同一性を保つ。毎レンダリングで別の関数を渡すと走査が
 * 再起動し、カメラが開き直る。遷移はページの再読み込みなので、モーダルを閉じる必要もない。
 */
function joinScanned(roomCode: string): void {
  navigate(joinUrl(roomCode));
}

/**
 * 入口。卓を立てるか、ルームコードで参加するかを選ぶ。
 *
 * ルーム作成は中央ディスプレイの役割なので、作成後はそのまま中央ディスプレイの卓画面へ
 * 遷移する（要件1.1）。
 */
export function LandingScreen(): React.ReactElement {
  // 入力欄の値は文字列のまま持つ。途中の値（空欄など）を数値へ丸めると打ち直せなくなる。
  const [smallBlindText, setSmallBlindText] = useState<AmountValue>(50);
  const [bigBlindText, setBigBlindText] = useState<AmountValue>(100);
  const [defaultBuyInText, setDefaultBuyInText] = useState<AmountValue>(10_000);
  /** null の間はスモールブラインドに追従する。多くの卓では最小のチップが SB と同額。 */
  const [chipUnitText, setChipUnitText] = useState<AmountValue | null>(null);

  const smallBlind = parseAmount(smallBlindText);
  const bigBlind = parseAmount(bigBlindText);
  const defaultBuyIn = parseAmount(defaultBuyInText);
  const chipUnitValue = chipUnitText ?? smallBlindText;
  const chipUnit = parseAmount(chipUnitValue);

  const missing = (v: number | null): string | null => (v === null ? '入力してください' : null);
  // サーバのルーム作成の検証と同じ判定。
  const configIssue =
    smallBlind === null || bigBlind === null || defaultBuyIn === null || chipUnit === null
      ? null
      : roomConfigProblem({ smallBlind, bigBlind, defaultBuyIn, chipUnit });
  const canCreate =
    smallBlind !== null &&
    bigBlind !== null &&
    defaultBuyIn !== null &&
    chipUnit !== null &&
    configIssue === null;
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);

  const onCreate = (): void => {
    if (smallBlind === null || bigBlind === null || defaultBuyIn === null || chipUnit === null) {
      return;
    }
    setBusy(true);
    setError(null);
    void createRoom({ smallBlind, bigBlind, defaultBuyIn, chipUnit })
      .then((res) => navigate(displayUrl(res.code)))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'ルームの作成に失敗しました');
        setBusy(false);
      });
  };

  const trimmed = code.trim().toUpperCase();

  return (
    <Container size="sm" pt="xl" className="safe-bottom">
      <Stack gap="xl">
        <Stack gap={4}>
          <AppTitle order={1} />
          <Text c="dimmed">物理カード × デジタルチップの No-Limit Hold&rsquo;em</Text>
        </Stack>

        {error !== null && (
          <Alert color="red" title="エラー">
            {error}
          </Alert>
        )}

        {/* 卓を立てるのはタブレット / PC のみ。スマホには参加の経路だけを出す。 */}
        <Stack gap="xl" className={classes.createTable} data-testid="create-table">
          <Card withBorder padding="lg">
            <Stack>
              <Title order={3}>卓を立てる</Title>
              <Text size="sm" c="dimmed">
                この端末が中央ディスプレイになります。参加用の QR コードを表示します。
              </Text>
              <Group grow>
                <AmountInput
                  label="スモールブラインド"
                  value={smallBlindText}
                  onChange={setSmallBlindText}
                  step={10}
                  allowNegative={false}
                  problem={missing(smallBlind)}
                />
                <AmountInput
                  label="ビッグブラインド"
                  value={bigBlindText}
                  onChange={setBigBlindText}
                  step={10}
                  allowNegative={false}
                  problem={missing(bigBlind)}
                />
              </Group>
              <Group grow align="flex-start">
                <AmountInput
                  label="デフォルトバイイン"
                  description="着席時の初期値"
                  value={defaultBuyInText}
                  onChange={setDefaultBuyInText}
                  step={1000}
                  allowNegative={false}
                  problem={missing(defaultBuyIn)}
                />
                <AmountInput
                  label="チップ単位"
                  description="ベット・バイインはこの倍数"
                  value={chipUnitValue}
                  onChange={setChipUnitText}
                  allowNegative={false}
                  problem={missing(chipUnit)}
                  data-testid="create-chip-unit"
                />
              </Group>
              <Button onClick={onCreate} loading={busy} disabled={!canCreate}>
                ルームを作成
              </Button>
              {configIssue !== null && (
                <Text size="sm" c="red" data-testid="create-config-problem">
                  {configIssue}
                </Text>
              )}
            </Stack>
          </Card>

          <Divider label="または" />
        </Stack>

        <Card withBorder padding="lg">
          <Stack>
            <Title order={3}>参加する</Title>
            <Text size="sm" c="dimmed">
              中央ディスプレイの QR コードを読み取るか、ルームコードを入力してください。
            </Text>

            {/* 読み取りを先に置く。コードを目で追って打ち込むより速い。 */}
            <Button data-testid="scan-qr" onClick={() => setScanOpen(true)}>
              QR コードを読み取る
            </Button>

            <Divider label="コードを入力する" labelPosition="center" />

            <TextInput
              label="ルームコード"
              placeholder="ABCDEF"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              maxLength={6}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              styles={{ input: { textTransform: 'uppercase', letterSpacing: '0.2em' } }}
            />
            <Button
              variant="light"
              disabled={trimmed.length !== 6}
              onClick={() => navigate(joinUrl(trimmed))}
            >
              この卓に参加
            </Button>
          </Stack>
        </Card>
      </Stack>

      <QrScanner opened={scanOpen} onClose={() => setScanOpen(false)} onDetect={joinScanned} />
    </Container>
  );
}
